// user.effects.ts
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { of, from, firstValueFrom } from 'rxjs';
import { map, catchError, tap, switchMap, withLatestFrom } from 'rxjs/operators';
import * as UserActions from '../actions/user.actions';
import { AppUserState, AppState } from '../states/app.state';
import { UAMAuthService } from 'src/app/shared/services/uam-auth.service';
import { UserSettingsService } from '../../services/user-settings.service';
import { isAllowedRubiRole } from 'src/app/shared/constants/rubi-allowed-roles';

@Injectable()
export class UserEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private uamAuthService: UAMAuthService,
    private userSettingsService: UserSettingsService,
    private router: Router
  ) { }

  loadCurrentUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.initApp, UserActions.loadCurrentUser),
      tap(() => { }),
      switchMap(() => from(this.loadUserData())),
      catchError((error) => {
        console.error('UserEffect: Error loading user:', error);
        return of(UserActions.clearCurrentUser());
      })
    )
  );

  private async loadUserData() {
    const returnPath =
      typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}`
        : '';

    const isAuthenticated = await firstValueFrom(
      this.uamAuthService.checkAuthentication()
    );
    if (!isAuthenticated) {
      console.error('[UAM Redirect] Session not valid:', {
        returnPath,
      });
      this.uamAuthService.navigateToUamLogin(returnPath);
      return UserActions.clearCurrentUser();
    }

    const user = this.uamAuthService.getAuthenticatedUser() as Record<
      string,
      unknown
    > | null;

    if (!user || !user['sub']) {
      console.error('[UAM Redirect] No user or missing sub:', {
        hasUser: !!user,
        hasSub: !!user?.['sub'],
      });
      this.uamAuthService.navigateToUamLogin(returnPath);
      return UserActions.clearCurrentUser();
    }

    const rubiRoleClaim = user['custom:rubi_role'];
    if (!rubiRoleClaim) {
      console.error('[UserEffect] Sign-in could not be completed.');
      this.uamAuthService.navigateToUamLogin(returnPath);
      return UserActions.clearCurrentUser();
    }

    if (!isAllowedRubiRole(String(rubiRoleClaim))) {
      console.error('[UserEffect] This account is not authorized for this application.');
      const deniedViewUser: AppUserState = {
        sub: String(user['sub']),
        name: typeof user['name'] === 'string' ? user['name'] : '',
        lastName:
          typeof user['family_name'] === 'string' ? user['family_name'] : '',
        role: rubiRoleClaim as string,
        email:
          typeof user['email'] === 'string' ? user['email'] : undefined,
        organization: '',
      };
      await this.router.navigateByUrl('/access-denied');
      return UserActions.setCurrentUser({ currentUser: deniedViewUser });
    }

    const userSettings = await this.userSettingsService.getMe().catch((error: unknown) => {
      console.error(
        '[UserEffect] getMe failed; organization and settings will be empty until the request succeeds.',
        error
      );
      return null;
    });

    const currentUser: AppUserState = {
      sub: String(user['sub']),
      name: typeof user['name'] === 'string' ? user['name'] : '',
      lastName:
        typeof user['family_name'] === 'string' ? user['family_name'] : '',
      role: rubiRoleClaim as string,
      email:
        typeof user['email'] === 'string' ? user['email'] : undefined,
      settings: userSettings,
      organization: userSettings?.organization || '',
    };

    return UserActions.setCurrentUser({ currentUser });
  }

  private saveUserToLocalStorage(user: AppUserState): void {
    try {
      localStorage.setItem('currentUser', JSON.stringify(user));
    } catch (error) {
      console.error('UserEffect: Error saving user to localStorage:', error);
    }
  }

  private clearUserFromLocalStorage(): void {
    try {
      localStorage.removeItem('currentUser');
    } catch (error) {
      console.error('UserEffect: Error clearing user from localStorage:', error);
    }
  }

  setCurrentUser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.setCurrentUser),
        tap(({ currentUser }) => {
          this.saveUserToLocalStorage(currentUser);
        })
      ),
    { dispatch: false }
  );

  updateUserSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateUserSettings),
      withLatestFrom(this.store.pipe(select((state: AppState) => state.currentUser))),
      map(([{ settings }, currentUser]) => {
        const updatedUser: AppUserState = {
          ...currentUser,
          settings: { ...currentUser.settings, ...settings }
        };
        return UserActions.setCurrentUser({ currentUser: updatedUser });
      })
    )
  );

  clearCurrentUser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.clearCurrentUser),
        tap(() => {
          this.clearUserFromLocalStorage();
        })
      ),
    { dispatch: false }
  );
}
